import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "./Navigation";
import Footer from "./Footer";
import LightRays from "./LightRays";
import QRCodePopup from "./QRCodePopup";
import { useCapacitySync } from "../hooks/useCapacitySync";

const ManageReservation = () => {
  // Auto-sync capacity when page loads
  const { isSyncing, syncResult } = useCapacitySync(true, 500);

  const [teamName, setTeamName] = useState("");
  const [password, setPassword] = useState("");
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [qrPopupOpen, setQrPopupOpen] = useState(false);
  const [qrReservationData, setQrReservationData] = useState(null);
  const [timeChangeModal, setTimeChangeModal] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState({});
  const [capacityInfo, setCapacityInfo] = useState({
    table1: {},
    table2: {},
    table3: {}
  });

  const showQRCode = (reservation) => {
    setQrReservationData({
      id: reservation.id,
      team_name: reservation.teamName,
      team_number: reservation.participantCount,
      time_slot: reservation.time_slot, // Use original time_slot from database
      password: reservation.password, // Include password from reservation data
      table: reservation.table // Pass table information
    });
    setQrPopupOpen(true);
  };

  const fetchTimeAvailability = async () => {
    try {
      // Fetch availability for all three tables
      const [table1Response, table2Response, table3Response] = await Promise.all([
        fetch('/backend/get_time_availability.php'),
        fetch('/backend/get_time_availability_mira.php'),
        fetch('/backend/get_time_availability_third.php')
      ]);

      if (table1Response.ok && table2Response.ok && table3Response.ok) {
        const [table1Data, table2Data, table3Data] = await Promise.all([
          table1Response.json(),
          table2Response.json(),
          table3Response.json()
        ]);

        setAvailableTimeSlots({
          table1: table1Data.availability || {},
          table2: table2Data.availability || {},
          table3: table3Data.availability || {}
        });

        setCapacityInfo({
          table1: table1Data.capacity_info || {},
          table2: table2Data.capacity_info || {},
          table3: table3Data.capacity_info || {}
        });
      }
    } catch (err) {
      console.error('Error fetching time availability:', err);
    }
  };

  const handleTeamNameSubmit = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) {
      setError("Prosím zadejte název týmu");
      return;
    }
    if (!password.trim()) {
      setError("Prosím zadejte heslo");
      return;
    }
    if (password.length < 4) {
      setError("Heslo musí mít alespoň 4 znaky");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setHasSearched(true);

    try {
      // Fetch from all three tables by team name and password
      const [response1, response2, response3] = await Promise.all([
        fetch(`/backend/get_reservations.php?team_name=${encodeURIComponent(teamName)}&password=${encodeURIComponent(password)}`),
        fetch(`/backend/get_reservations_mira.php?team_name=${encodeURIComponent(teamName)}&password=${encodeURIComponent(password)}`),
        fetch(`/backend/get_reservations_third.php?team_name=${encodeURIComponent(teamName)}&password=${encodeURIComponent(password)}`)
      ]);

      let allReservations = [];
      let totalCount = 0;

      if (response1.ok) {
        const data1 = await response1.json();
        const reservations1 = data1.reservations.map(r => ({ ...r, table: 'reservations' }));
        allReservations = [...allReservations, ...reservations1];
        totalCount += data1.count;
      }

      if (response2.ok) {
        const data2 = await response2.json();
        const reservations2 = data2.reservations.map(r => ({ ...r, table: 'reservations_mira' }));
        allReservations = [...allReservations, ...reservations2];
        totalCount += data2.count;
      }

      if (response3.ok) {
        const data3 = await response3.json();
        const reservations3 = data3.reservations.map(r => ({ ...r, table: 'reservations_third' }));
        allReservations = [...allReservations, ...reservations3];
        totalCount += data3.count;
      }

      setReservations(allReservations);
      setSuccess(`Našli jsme ${totalCount} rezervací pro tým "${teamName}" (${allReservations.filter(r => r.table === 'reservations').length} ze Stezky záhrobím, ${allReservations.filter(r => r.table === 'reservations_mira').length} z Domu šepotů, ${allReservations.filter(r => r.table === 'reservations_third').length} z Prokleté komnaty)`);
      
      // Fetch time availability after getting reservations
      await fetchTimeAvailability();
    } catch (err) {
      setError("Chyba při načítání rezervací. Zkuste to prosím znovu.");
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = async (reservation, newTimeSlot) => {
    try {
      // Determine which endpoint to use based on table name
      let changeEndpoint;
      if (reservation.table === 'reservations_mira') {
        changeEndpoint = '/backend/time_change_mira.php';
      } else if (reservation.table === 'reservations_third') {
        changeEndpoint = '/backend/time_change_third.php';
      } else {
        changeEndpoint = '/backend/time_change.php';
      }

      const response = await fetch(changeEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reservationId: reservation.id,
          newTimeSlot: newTimeSlot,
          password: password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Chyba při změně času rezervace');
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        // Update the reservation in the local state
        setReservations((prev) =>
          prev.map((res) => 
            res.id === reservation.id 
              ? { ...res, time_slot: newTimeSlot, timeDisplay: newTimeSlot }
              : res
          )
        );
        
        // Refresh time availability
        await fetchTimeAvailability();
        setTimeChangeModal(null);
        setSuccess("Čas rezervace byl úspěšně změněn");
        setError(''); // Clear any previous errors
      } else {
        setError(data.error || 'Chyba při změně času rezervace');
      }
      
    } catch (err) {
      setError('Chyba sítě. Zkuste to prosím znovu.');
    }
  };

  const handleCancelReservation = async (reservationId, table) => {
    if (!window.confirm("Opravdu chcete zrušit tuto rezervaci?")) {
      return;
    }

    try {
      let endpoint;
      if (table === 'reservations_mira') {
        endpoint = './backend/cancel_reservation_mira.php';
      } else if (table === 'reservations_third') {
        endpoint = './backend/cancel_reservation_third.php';
      } else {
        endpoint = './backend/cancel_reservation.php';
      }
        
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          reservationId: reservationId,
          password: password
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to cancel reservation");
      }

      const data = await response.json();

      setReservations((prev) =>
        prev.map((res) => (res.id === reservationId ? { ...data.reservation, table: table } : res))
      );
      setSuccess("Rezervace byla úspěšně zrušena");
    } catch (err) {
      setError(
        err.message || "Chyba při rušení rezervace. Zkuste to prosím znovu."
      );
    }
  };

  return (
    <div className="min-h-screen bg-black relative flex flex-col">
      {/* Light Rays Background */}
      <div className="fixed inset-0 w-full h-full" style={{ zIndex: 1 }}>
        <LightRays
          raysOrigin="top-center"
          raysColor="#00ff41"
          raysSpeed={1.5}
          lightSpread={0.8}
          rayLength={2.5}
          fadeDistance={2.0}
          followMouse={true}
          mouseInfluence={0.1}
          noiseAmount={0.1}
          distortion={0.05}
          className="custom-rays"
        />
      </div>
      
      {/* Creepy Background Elements */}
      <div className="absolute inset-0 opacity-10" style={{ zIndex: 2 }}>
        <div className="absolute top-20 left-10 w-32 h-32 border border-green-500 rounded-full animate-beetlejuice-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 border border-green-400 rounded-full animate-beetlejuice-pulse" style={{ animationDelay: "1s" }}></div>
        <div className="absolute bottom-20 left-1/4 w-16 h-16 border border-green-600 rounded-full animate-beetlejuice-pulse" style={{ animationDelay: "2s" }}></div>
        <div className="absolute top-1/2 right-1/3 w-20 h-20 border border-green-300 rounded-full animate-beetlejuice-pulse" style={{ animationDelay: "3s" }}></div>
      </div>

      {/* Navigation */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        <Navigation />
      </div>

      {/* Main Content */}
      <div className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" style={{ position: 'relative', zIndex: 10 }}>
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-green-400 mb-4 drop-shadow-2xl">
            🦇 SPRÁVA REZERVACÍ 🦇
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Zadejte název vašeho týmu pro zobrazení a správu vašich rezervací
          </p>
        </div>

        {/* Capacity Sync Status */}
        {isSyncing && (
          <div className="mb-6 bg-blue-900/30 border-2 border-blue-600 rounded-2xl p-4 backdrop-blur-sm max-w-4xl mx-auto">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full"></div>
                <p className="text-blue-200 text-sm">
                  🔄 Synchronizing capacity data...
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-gray-900/80 border-2 border-green-800 rounded-3xl shadow-2xl p-8 backdrop-blur-sm beetlejuice-glow">
          {/* Team Name Search Form */}
          <form onSubmit={handleTeamNameSubmit} className="mb-8">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-green-300 mb-2">
                    Název týmu:
                  </label>
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-green-600 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-800 text-white placeholder-gray-400"
                    placeholder="Zadejte název týmu použitý při registraci"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-green-300 mb-2">
                    🔐 Heslo:
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength="4"
                    className="w-full px-4 py-3 border border-green-600 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-800 text-white placeholder-gray-400"
                    placeholder="Zadejte heslo použitý při registraci"
                  />
                </div>
              </div>
              <div className="flex items-end sm:items-end sm:justify-start justify-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="beetlejuice-green text-black px-8 py-3 rounded-xl font-semibold beetlejuice-hover disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none beetlejuice-border beetlejuice-glow"
                >
                  {loading ? "🔍 Hledám v pekle..." : "💀 Najít Rezervaci"}
                </button>
              </div>
            </div>
          </form>

          {/* Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-900/50 border border-red-600 rounded-xl backdrop-blur-sm">
              <p className="text-red-300 font-medium">💀 {error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-900/50 border border-green-600 rounded-xl backdrop-blur-sm">
              <p className="text-green-300 font-medium">👻 {success}</p>
            </div>
          )}

          {/* Reservations List */}
          {reservations.length > 0 && (
            <div className="space-y-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-green-400 mb-2 drop-shadow-lg">
                  🦇 Vaše Rezervace
                </h2>
              </div>

              {reservations.map((reservation) => (
                <div
                  key={reservation.id}
                  className={`p-6 rounded-2xl border-2 transition-all duration-200 ${
                    reservation.status === "cancelled"
                      ? "bg-gray-800 border-gray-600 opacity-60"
                      : "bg-gradient-to-r from-green-900/50 to-green-800/50 border-green-600 hover:shadow-2xl hover:shadow-green-500/20 beetlejuice-glow"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                        <h3 className="text-xl font-bold text-green-300">
                          💀 {reservation.teamName}
                        </h3>
                        <div className="flex gap-2">
                          {reservation.status === "cancelled" && (
                            <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-900/50 text-red-300 border border-red-600">
                              ⚰️ Zrušeno
                            </span>
                          )}
                          <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-900/50 text-blue-300 border border-blue-600">
                            {reservation.table === 'reservations_mira' ? '👻 Dům šepotů' : 
                             reservation.table === 'reservations_third' ? '🏚️ Prokletá komnata' : '🦇 Stezka záhrobím'}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="font-semibold text-green-300">
                            ⏰ Čas:
                          </span>
                          <p className="text-gray-300 mt-1">
                            {reservation.timeDisplay}
                          </p>
                        </div>
                        <div>
                          <span className="font-semibold text-green-300">
                            👥 Počet účastníků:
                          </span>
                          <p className="text-gray-300">
                            {reservation.participantCount}
                          </p>
                        </div>
                      </div>
                    </div>

                    {reservation.status === "active" && (
                      <div className="flex flex-col gap-3 min-w-[200px]">
                        <button
                          onClick={() => setTimeChangeModal(reservation)}
                          className="beetlejuice-green text-black px-4 py-2 rounded-lg font-medium beetlejuice-hover beetlejuice-border text-sm flex items-center justify-center gap-2"
                        >
                          ⏰ Změnit Čas
                        </button>
                        <button
                          onClick={() => showQRCode(reservation)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-lg transform hover:scale-105 border border-blue-400 hover:border-blue-300 text-sm"
                        >
                          📱 QR Kód
                        </button>
                        <button
                          onClick={() =>
                            handleCancelReservation(reservation.id, reservation.table)
                          }
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-lg transform hover:scale-105 border border-red-400 hover:border-red-300 text-sm"
                        >
                          ⚰️ Zrušit
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Reservations Found */}
          {reservations.length === 0 && hasSearched && !loading && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-800 border-2 border-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💀</span>
              </div>
              <h3 className="text-xl font-semibold text-green-400 mb-2 drop-shadow-lg">
                ⚰️ Žádná rezervace nenalezena
              </h3>
              <p className="text-gray-300">
                Pro tým <strong className="text-green-300">{teamName}</strong> nebyla nalezena žádná
                rezervace.
              </p>
            </div>
          )}
        </div>

        {/* Back to Reservation Button */}
        <div className="text-center mt-8">
          <Link
            to="/reservations"
            className="inline-block beetlejuice-green text-black px-8 py-3 rounded-xl font-semibold beetlejuice-hover beetlejuice-border beetlejuice-glow"
          >
            🦇 Vytvořit Novou Rezervaci
          </Link>
        </div>
      </div>

      <Footer />

      {/* QR Code Popup */}
      <div style={{ position: 'relative', zIndex: 20 }}>
        {/* Time Change Modal */}
        {timeChangeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-gray-900 border-2 border-green-600 rounded-2xl p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-green-400 mb-4 drop-shadow-lg">
                ⏰ Změnit Čas Hrůzy
              </h3>
              <p className="text-gray-300 mb-2">
                Vyberte nový čas pro <strong className="text-green-300">{timeChangeModal.teamName}</strong>
              </p>
              <p className="text-sm text-gray-400 mb-4">
                Tým má {timeChangeModal.participantCount} {timeChangeModal.participantCount === 1 ? 'člena' : 'členy'}. 
                Zelené časy mají dostatečnou kapacitu.
              </p>
              
              <div className="grid grid-cols-3 gap-2 mb-6 max-h-60 overflow-y-auto">
                {(() => {
                  // Different time slots based on table
                  let timeSlots;
                  if (timeChangeModal.table === 'reservations_mira') {
                    // 15-minute intervals for second reservation (excluding :45)
                    timeSlots = [
                      '15:00', '15:15', '15:30',
                      '16:00', '16:15', '16:30',
                      '17:00', '17:15', '17:30',
                      '18:00', '18:15', '18:30',
                      '19:00', '19:15', '19:30'
                    ];
                  } else {
                    // 10-minute intervals for first and third reservations
                    timeSlots = [
                      '15:00', '15:10', '15:20', '15:30', '15:40', '15:50',
                      '16:00', '16:10', '16:20', '16:30', '16:40', '16:50',
                      '17:00', '17:10', '17:20', '17:30', '17:40', '17:50',
                      '18:00', '18:10', '18:20', '18:30', '18:40', '18:50',
                      '19:00', '19:10', '19:20', '19:30', '19:40', '19:50'
                    ];
                  }
                  return timeSlots;
                })().map((timeSlot) => {
                  const isCurrentSlot = timeSlot === timeChangeModal.time_slot;
                  let isAvailable;
                  let capacityData;
                  
                  if (timeChangeModal.table === 'reservations_mira') {
                    isAvailable = availableTimeSlots.table2?.[timeSlot];
                    capacityData = capacityInfo.table2?.[timeSlot];
                  } else if (timeChangeModal.table === 'reservations_third') {
                    isAvailable = availableTimeSlots.table3?.[timeSlot];
                    capacityData = capacityInfo.table3?.[timeSlot];
                  } else {
                    isAvailable = availableTimeSlots.table1?.[timeSlot];
                    capacityData = capacityInfo.table1?.[timeSlot];
                  }
                  
                  // Check if there's enough capacity for the team size
                  const availableSpots = capacityData?.available_spots || 0;
                  const teamSize = timeChangeModal.participantCount || 1;
                  const hasEnoughCapacity = availableSpots >= teamSize;
                  
                  const isDisabled = isCurrentSlot || !isAvailable || !hasEnoughCapacity;
                  
                  
                  // Get capacity display text
                  const maxCapacity = capacityData?.max_capacity || (timeChangeModal.table === 'reservations_mira' ? 4 : timeChangeModal.table === 'reservations_third' ? 2 : 8);
                  const capacityText = capacityData ? `(${availableSpots}/${maxCapacity})` : '';
                  
                  return (
                    <button
                      key={timeSlot}
                      onClick={() => !isDisabled && handleTimeChange(timeChangeModal, timeSlot)}
                      disabled={isDisabled}
                      className={`px-3 py-2 text-sm rounded-md transition duration-200 ${
                        isCurrentSlot
                          ? 'bg-red-100 text-red-800 cursor-default border border-red-400'
                          : isAvailable && hasEnoughCapacity
                          ? 'bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer border border-green-400'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-400'
                      }`}
                      title={
                        isCurrentSlot 
                          ? 'Aktuální čas' 
                          : !isAvailable 
                          ? 'Čas není dostupný' 
                          : !hasEnoughCapacity 
                          ? `Nedostatečná kapacita (potřebujete ${teamSize} míst, dostupné: ${availableSpots})`
                          : `Dostupné: ${availableSpots}/${maxCapacity} míst`
                      }
                    >
                      <div className="flex flex-col items-center">
                        <span>{timeSlot}</span>
                        {capacityText && (
                          <span className="text-xs opacity-75">{capacityText}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setTimeChangeModal(null)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition duration-200 border border-gray-500"
                >
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QR Code Popup */}
        <QRCodePopup 
          isOpen={qrPopupOpen}
          onClose={() => setQrPopupOpen(false)}
          reservationData={qrReservationData}
        />
      </div>
    </div>
  );
};

export default ManageReservation;
